import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Hook to validate if user has completed required onboarding information
 *
 * @returns {Object} Validation result with:
 * - isComplete: boolean - Whether all required fields are filled
 * - missingFields: Array - List of missing required fields
 * - missingSteps: Array - List of incomplete onboarding steps
 * - redirectPath: string - Path to redirect for completing onboarding
 */
export const useOnboardingValidation = () => {
  const { user, loading } = useAuth();

  const validation = useMemo(() => {
    // If still loading, return neutral state (don't redirect yet)
    if (loading) {
      return {
        isComplete: true, // Don't redirect while loading
        missingFields: [],
        missingSteps: [],
        redirectPath: null,
      };
    }

    // If no user after loading, user is not authenticated
    if (!user) {
      return {
        isComplete: false,
        missingFields: [],
        missingSteps: [],
        redirectPath: "/login",
      };
    }

    const role = String(user.role || "").toLowerCase();
    const missingFields = [];
    const missingSteps = [];

    if (role === "client") {
      let meta = null;
      try {
        meta = user.meta ? (typeof user.meta === "string" ? JSON.parse(user.meta) : user.meta) : null;
      } catch (_) {
        meta = null;
      }

      if (!meta || typeof meta !== "object") {
        missingFields.push("meta");
        missingSteps.push(1);
      }

      // Client Step 1: City is required (step 2 categories are optional)
      if (!meta || !meta.city) {
        missingFields.push("city");
        missingSteps.push(1);
      }

      // Determine redirect path based on first missing step
      if (missingSteps.length > 0) {
        const firstMissingStep = Math.min(...missingSteps);
        return {
          isComplete: false,
          missingFields,
          missingSteps,
          redirectPath: `/auth/onboarding/client?step=${firstMissingStep}`,
        };
      }
    } else if (role === "business") {
      // Business onboarding: user must have at least one business (owned or via membership)
      const hasBusinesses = (user.businesses?.length > 0) || (user.memberships?.length > 0);
      const hasLegacyBusiness = user.business && user.business.id;
      if (!hasBusinesses && !hasLegacyBusiness) {
        missingFields.push("business");
        missingSteps.push(1);
        return {
          isComplete: false,
          missingFields,
          missingSteps,
          redirectPath: "/auth/onboarding/business",
        };
      }
    }

    return {
      isComplete: true,
      missingFields: [],
      missingSteps: [],
      redirectPath: null,
    };
  }, [user, loading]);

  return validation;
};
